// =============================================================================
// "New version available" prompt, and the machinery to actually notice one.
//
// The service worker precaches the shell so the app opens instantly from a home
// screen (§2.2). The cost is that a deploy does not reach a desk until the
// browser notices a new worker — and browsers only look on their own schedule.
// Safari in particular may not look for hours, which meant a fix could be live
// and invisible, and staff were told to "close it and open it again".
//
// So this does three things rather than one:
//   1. offers the update instead of taking it (a reload mid-transaction would
//      lose a basket of scanned books and look like a crash)
//   2. exposes a manual check, so Settings can show the version and a button
//      rather than anyone having to clear a cache
//
// There is deliberately NO polling. registerSW checks once when the app starts,
// which covers the ordinary case of opening it in the morning, and Settings
// covers the rest. A timer would be near-free — a conditional request for
// sw.js, and no prompt unless a version genuinely exists — but a background
// check nobody asked for is a thing to explain and a thing to distrust, and the
// desk would rather press a button.
// =============================================================================

import { el } from './dom.js';
import { T } from './strings.js';

// Injected at build time — see vite.config.js.
export const BUILD_ID = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

let registration = null;      // ServiceWorkerRegistration, once registered
let applyUpdate = null;       // updateSW(true) — activates the waiting worker
let updateReady = false;      // a new version is waiting right now
const listeners = new Set();  // Settings subscribes to hear about a find

// Ask the browser to re-check for a new worker. Safe to call often: it is a
// conditional request for sw.js, not a download of the app.
export async function checkForUpdate() {
  if (!registration) return false;
  try {
    await registration.update();
  } catch {
    return updateReady;       // offline, usually — not worth surfacing
  }
  return updateReady;
}

export const isUpdateReady = () => updateReady;
export function onUpdateFound(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Apply and reload. Exposed so Settings can offer the same action as the bar.
export function applyPendingUpdate() {
  if (applyUpdate) applyUpdate(true);
}

export async function mountUpdatePrompt() {
  if (!('serviceWorker' in navigator)) return;

  let registerSW;
  try {
    ({ registerSW } = await import('virtual:pwa-register'));
  } catch {
    return;                   // plugin not active (dev server)
  }

  const bar = el('div', { class: 'update-bar hidden', role: 'status' });
  document.body.append(bar);

  const updateSW = registerSW({
    immediate: true,

    // Registration itself performs one check, at app start. That is the only
    // automatic one; everything else is Settings → Check for updates.
    onRegisteredSW(_swUrl, r) {
      registration = r || null;
    },

    onNeedRefresh() {
      updateReady = true;
      applyUpdate = updateSW;
      for (const fn of listeners) { try { fn(); } catch { /* a listener must not break the bar */ } }

      const reload = el('button', { class: 'btn btn-sm' }, T.updateNow);
      reload.addEventListener('click', () => {
        reload.disabled = true;
        reload.textContent = T.updating;
        updateSW(true);
      });
      bar.replaceChildren(el('span', {}, T.updateAvailable), reload);
      bar.classList.remove('hidden');
    },
  });

  applyUpdate = updateSW;
}
